import { ChangeDetectionStrategy, Component, DestroyRef, ViewContainerRef, computed, inject, signal } from '@angular/core';
import type { Signal, TemplateRef } from '@angular/core';
import { HlmBadge } from '@lite-workspace/shared/helm-ui/badge';
import { NgTemplateOutlet } from '@angular/common';
import { IS_MOBILE } from '@lite-workspace/utils';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { type LightboxItem, PhotoLightboxComponent } from '@lite-workspace/shared/photo-lightbox';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import {
  licenceReachesProject,
  type MyTemplate,
  type PartnerCardPack,
  type TemplateLicenceType,
  type UploadedTemplate,
} from '../domain/entities/design.models';
import { partnerLicenceBadge, partnerPriceLabel } from './partner-template-card.component';
import { PartnerLicenceService } from './partner-licence.service';
import { pendingPackItems, templateLightboxItems } from './template-lightbox-items';

export interface PartnerPackPreviewContext {
  readonly pack: PartnerCardPack;
  /** Project the pack is being looked at FROM (the editor). Absent on the templates screen: there the
   *  question «does this licence reach the open project» is not asked at all. */
  readonly projectId?: string;
  /** Развороты пака. Просмотрщик открывается СРАЗУ, на заглушках, и дочитывает их за собой — поэтому
   *  сюда приезжает не список, а чтение; сами ресурсы держит вызвавший экран. */
  readonly loadSpreads: () => Promise<readonly UploadedTemplate[]>;
  /** Живая лицензия фотографа на этот пак: полоса кнопок меняется сама, как только заявка оформлена. */
  readonly licence: Signal<MyTemplate | null>;
  /** The templates screen's own-pack action pair («Редактировать», «Использовать шаблон»): a live licence
   *  gets the very same one. Absent (editor panel) = a held pack shows no actions here. */
  readonly actions?: TemplateRef<PackViewerActionsContext>;
  /** Opened from the templates screen: its price buttons count in the sales funnel, the editor panel's do not. */
  readonly counted?: boolean;
  /** A free Lumir library pack: the one road in is this grant (resolves when the pack is already held).
   *  Present = no prices, one «Добавить в мои шаблоны» until the live licence lands. */
  readonly free?: () => Promise<void>;
}

export interface PackViewerActionsContext {
  readonly $implicit: MyTemplate;
  /** Closes the viewer before the picked road opens its own window or screen. */
  readonly dismiss: () => void;
}

const RENTED_MARK = { label: 'В аренде', variant: 'acc' } as const;

/**
 * The ONE viewer of another studio's pack — the templates screen and the editor's panel both open it.
 * It shows the pack's spreads and, when the photographer does not hold a usable licence, the two prices
 * that would give them one; a payment still awaiting the studio's word gets the wait explanation instead.
 * A live licence on the templates screen gets the own pack's action pair, handed in as `actions`.
 */
@Component({
  selector: 'app-partner-pack-preview-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PhotoLightboxComponent, NgTemplateOutlet, HlmBadge],
  // The viewer is its own fixed-position layer, so the dialog box around it must add no geometry.
  host: { class: 'contents' },
  template: `
    <!-- В полосе стоят только кнопки-действия, поэтому берём штатную компактную шапку: её собственные
         14px сверху и снизу дают полосу в 52px — высоту задаёт подпись слева (24px), а кнопка в 32px
         центрируется поверх неё абсолютом и получает ровно 10px запаса над и под собой. Своя ручка
         --glx-bar-pad-y экрану не нужна: штатное значение и есть нужное. Компактный режим заодно
         считает верх кадра от живой высоты полосы, поэтому разворот поднимается вслед за ней. -->
    <lib-photo-lightbox
      [compactBar]="true"
      [photos]="items()"
      [index]="index()"
      [canDownload]="false"
      maxStage="full"
      [single]="single()"
      [bottomBar]="isMobile()"
      [phoneChrome]="isMobile()"
      (indexChange)="index.set($event)"
      (viewerDismissed)="close()"
    >
      <span lightboxBarTitle class="contents">
        <!-- A cover-only pack has nothing to page through: the viewer drops counter and name, so the bar
             keeps the offer's name here, in the viewer's own name style. -->
        @if (single()) {
          <span class="glx-lb__name">{{ title }}</span>
        }
        @if (licenceMark(); as mark) {
          <span hlmBadge class="shrink-0" [variant]="mark.variant">{{ mark.label }}</span>
        }
      </span>
      <!-- Слот проекции адресуется статичной обёрткой: содержимое @if-блока сам селектор
           слота не видит, поэтому кнопки живут ВНУТРИ уже спроецированного узла.
           Phone: same buttons go to the bottom bar, away from the close ×, 44px tall; the bar replaces
           the thumbnail strip (swipe pages), a pair too wide for one row wraps. -->
      @if (isMobile()) {
        <div bottomBar class="flex w-full flex-wrap items-center gap-3 *:min-h-11 *:grow">
          <ng-container [ngTemplateOutlet]="actions" />
        </div>
      } @else {
        <div lightboxBarCenter class="contents"><ng-container [ngTemplateOutlet]="actions" /></div>
      }
    </lib-photo-lightbox>
    <ng-template #actions>
      @if (awaitingPayment()) {
        <button type="button" [class]="waitAction" (click)="explainWait()">Ждёт оплаты</button>
        <button type="button" [class]="cancelAction" (click)="cancelOrder()">Отменить заказ</button>
      } @else if (offersFree()) {
        <button type="button" [class]="filledAction" [disabled]="adding()" (click)="addFree()">Добавить в мои шаблоны</button>
      } @else if (purchasable()) {
        @if (pack.purchasePriceRub !== null) {
          <button type="button" [class]="filledAction" (click)="request('purchase')">{{ purchaseLabel }}</button>
        }
        @if (pack.rentPriceRub !== null) {
          <button type="button" [class]="filledAction" (click)="request('rental')">{{ rentLabel }}</button>
        }
      } @else if (usableLicence(); as held) {
        <ng-container [ngTemplateOutlet]="heldActions" [ngTemplateOutletContext]="{ $implicit: held, dismiss }" />
      }
    </ng-template>
  `,
})
export class PartnerPackPreviewDialogComponent {
  private readonly ctx = injectBrnDialogContext<PartnerPackPreviewContext>();
  private readonly dialogRef = inject(BrnDialogRef);
  private readonly vcr = inject(ViewContainerRef);
  private readonly licences = inject(PartnerLicenceService);
  private readonly toast = inject(ToastService);

  protected readonly pack = this.ctx.pack;
  protected readonly isMobile = inject(IS_MOBILE);
  protected readonly index = signal(0);
  private readonly spreads = signal<readonly UploadedTemplate[]>([]);
  private readonly loading = signal(true);

  // Ожидание оплаты — пара кнопок-таблеток: «Ждёт оплаты» ведёт в окно заказа, «Отменить заказ»
  // снимает его. Различаются они только заливкой, поэтому геометрия у них ОДНА: рост тот же, что у
  // ценовых кнопок (полоса не растёт), воздуха по горизонтали больше, радиус полный. Рамка входит в
  // общую геометрию, а не в обводной вариант: иначе обводная кнопка встала бы на 2px выше залитой.
  private readonly pendingPill =
    'rounded-full border px-6 py-1.5 text-sm font-semibold whitespace-nowrap transition-opacity duration-[var(--t-fast)]';

  protected readonly waitAction = `${this.pendingPill} border-transparent bg-acc text-on-acc hover:opacity-90`;
  protected readonly cancelAction = `${this.pendingPill} border-line3 text-ink hover:opacity-80`;

  // Обе цены — залитые кнопки: это единственное действие экрана, и цена читается целиком, без обрезки.
  // Геометрия — та же, что у обводной кнопки полосы: низкая по росту, с широким воздухом по горизонтали.
  protected readonly filledAction =
    'rounded-[var(--r-s)] bg-acc px-5 py-1.5 text-sm font-semibold text-on-acc whitespace-nowrap ' +
    'transition-opacity duration-[var(--t-fast)] hover:opacity-90';

  /** The bar names the offer, not the file: a foreign pack's PSD names mean nothing to the photographer. */
  protected readonly title = [this.ctx.pack.name, this.ctx.pack.studio?.name, this.ctx.pack.sizeLabel]
    .filter(Boolean)
    .join(' · ');

  protected readonly purchaseLabel = partnerPriceLabel('Купить', true, this.ctx.pack.purchasePriceRub);
  protected readonly rentLabel = partnerPriceLabel('Арендовать', true, this.ctx.pack.rentPriceRub);

  private readonly licence = this.ctx.licence;

  protected readonly awaitingPayment = computed(() => this.licence()?.status === 'pending');

  /** Prices are offered only when nothing the photographer already holds covers this project — a rental
   *  whose one credit went to another project is exactly as unusable here as no licence at all. */
  protected readonly purchasable = computed(() => {
    const licence = this.licence();
    return !licence || !licenceReachesProject(licence, this.ctx.projectId ?? null);
  });

  /** A library pack is never sold: until the live licence lands its one action is the free grant. */
  protected readonly offersFree = computed(() => !!this.ctx.free && !this.licence());
  protected readonly adding = signal(false);

  protected readonly heldActions = this.ctx.actions ?? null;
  protected readonly dismiss = (): void => this.close();

  /** Same gate as the card's menu: a spent rental credit or a wait offers no road into a project. */
  protected readonly usableLicence = computed(() => {
    const licence = this.licence();
    return this.heldActions && licence?.status === 'granted' && !this.purchasable() ? licence : null;
  });

  /** A held pack names its licence beside the title, in the card's own badge tone. */
  protected readonly licenceMark = computed(() => {
    const licence = this.licence();
    if (!this.heldActions || !licence || licence.type === 'free' || this.awaitingPayment() || this.purchasable()) return null;
    return licence.type === 'purchase' ? partnerLicenceBadge(licence) : RENTED_MARK;
  });

  /** A cover-only pack: nothing to page through, so no counter, arrows or strip. */
  protected readonly single = computed(() => this.items().length === 1);

  protected readonly items = computed<LightboxItem[]>(() =>
    this.loading()
      ? pendingPackItems(this.pack, this.title)
      : templateLightboxItems(this.spreads()).map(item => ({ ...item, name: this.title })),
  );

  /** The viewer can be dismissed while its spreads are still on the wire — a landing read must not then
   *  toast at a screen the photographer has already left, nor close a dialog somebody else opened since. */
  private alive = true;

  constructor() {
    inject(DestroyRef).onDestroy(() => (this.alive = false));
    void this.load();
  }

  // Оформление встаёт ПОВЕРХ просмотрщика: он держит тот самый ViewContainerRef, на котором открывается
  // следующий диалог, а его close() рушит вью не сразу — только после exit-анимации, унося с собой и
  // только что открытое окно. Закрыв просмотрщик, фотограф остаётся в паке и видит обновлённые кнопки.
  protected request(type: TemplateLicenceType): void {
    this.licences.request(this.vcr, this.pack, type, this.ctx.counted);
  }

  /** The bar flips to the held actions by itself once the reloaded list carries the licence; a refusal
   *  keeps the viewer open so the button can be pressed again. */
  protected async addFree(): Promise<void> {
    if (!this.ctx.free || this.adding()) return;
    this.adding.set(true);
    try {
      await this.ctx.free();
      // The grant is done but the list re-reads in the background: the button stays off until the licence
      // lands and the bar flips, so a second click cannot grant again and toast a second success.
      this.toast.success('Шаблон добавлен в «Мои шаблоны»');
    } catch {
      this.toast.error('Не удалось добавить шаблон — попробуйте ещё раз');
      this.adding.set(false);
    }
  }

  protected explainWait(): void {
    const licence = this.licence();
    if (!licence) return;
    this.licences.explainWait(this.vcr, licence).subscribe(cancelled => this.closeOnCancelled(cancelled));
  }

  protected cancelOrder(): void {
    const licence = this.licence();
    if (!licence) return;
    this.licences.cancelPending(this.vcr, licence).subscribe(cancelled => this.closeOnCancelled(cancelled));
  }

  // Снятый заказ уносит пак из «Моих шаблонов»: смотреть в просмотрщике больше нечего, и фотограф
  // возвращается к списку шаблонов. Служба отдаёт исход уже ПОСЛЕ уведомления — оно живёт на нашем
  // ViewContainerRef, и закройся просмотрщик раньше, Spartan снёс бы его вместе со своим вью.
  private closeOnCancelled(cancelled: boolean): void {
    if (cancelled) this.close();
  }

  protected close(): void {
    this.dialogRef.close();
  }

  private async load(): Promise<void> {
    let spreads: readonly UploadedTemplate[];
    try {
      spreads = await this.ctx.loadSpreads();
    } catch {
      if (!this.alive) return;
      this.toast.error('Не удалось загрузить развороты шаблона');
      this.close();
      return;
    }

    if (!this.alive) return;
    this.loading.set(false);
    this.spreads.set(spreads);
    if (templateLightboxItems(spreads).length === 0) {
      this.toast.info('В этом шаблоне пока нет разворотов с превью');
      this.close();
    }
  }
}
